<template>
  <div class="user-overview">
    <div class="row-3">
      <section
        v-for="panel in panels"
        :key="panel.key"
        class="user-panel"
        :class="'user-panel--' + panelMeta[panel.key].theme"
      >
        <h3 class="section-title"><span class="title-bar" />{{ panelMeta[panel.key].title }}</h3>
        <div class="user-panel-summary">
          <span class="summary-label">{{ panelMeta[panel.key].summaryLabel }}</span>
          <span class="summary-num">{{ fmtNum(panel.total) }}</span>
          <span class="summary-unit">户</span>
        </div>
        <div class="user-panel-divider" role="separator" />
        <div class="user-panel-body">
          <div class="user-item-list">
            <div v-for="item in panel.items" :key="item.key" class="user-item-row">
              <span class="user-item-icon">
                <dashboard-icons :name="itemMeta[item.key].icon" size="18" :color="itemMeta[item.key].iconColor" />
              </span>
              <span class="user-item-label">{{ itemMeta[item.key].label }}</span>
              <span class="user-item-value">
                <span class="field-value">{{ fmtNum(item.count) }}</span>
                <span class="field-unit">户</span>
              </span>
              <span class="user-item-ratio" :style="{ color: itemMeta[item.key].ratioColor }">占比 {{ item.ratio.toFixed(2) }}%</span>
            </div>
          </div>
          <div
            class="user-donut"
            :style="donutStyle(panel.items)"
            role="img"
            :aria-label="panelMeta[panel.key].title + '占比分布'"
          />
        </div>
      </section>
    </div>

    <section class="user-chart-section">
      <h3 class="section-title"><span class="title-bar" />今年新增客户行业分布</h3>
      <div ref="industryChart" class="user-industry-chart" />
    </section>
  </div>
</template>

<script>
import { markRaw } from 'vue'
import * as echarts from 'echarts'
import { ElMessage } from 'element-plus'
import DashboardIcons from './DashboardIcons.vue'
import { getDashboardUserOverview, getDashboardUserIndustryChart } from '@/api/dashboard'

/** 面板展示配置（前端写死，后端只返回业务数据） */
const PANEL_META = {
  inService: { title: '在服务客户总数', summaryLabel: '人数合计', theme: 'blue' },
  yearNew: { title: '今年新增客户', summaryLabel: '今年新增客户数', theme: 'blue' },
  monthNew: { title: '本月新增客户', summaryLabel: '本月新增客户数', theme: 'blue' },
}

/** 客户类型展示配置（前端写死） */
const ITEM_META = {
  time: { label: '按时间租赁客户数', icon: 'time', iconColor: '#fa8c16', ratioColor: '#fa8c16' },
  traffic: { label: '按流量租赁客户数', icon: 'traffic', iconColor: '#1890ff', ratioColor: '#1890ff' },
  service: { label: '购买服务包客户数', icon: 'service', iconColor: '#9254de', ratioColor: '#9254de' },
  buyout: { label: '买断型客户数', icon: 'buyout', iconColor: '#52c41a', ratioColor: '#52c41a' },
}

export default {
  name: 'DashboardUserOverview',
  components: { DashboardIcons },
  data() {
    return {
      panelMeta: PANEL_META,
      itemMeta: ITEM_META,
      panels: [],
      industryChart: { categories: [], rows: [] },
      industryChartInst: null,
      chartResizeHandler: null,
    }
  },
  mounted() {
    this.loadData()
    this.chartResizeHandler = () => {
      if (this.industryChartInst) this.industryChartInst.resize()
    }
    window.addEventListener('resize', this.chartResizeHandler)
  },
  beforeUnmount() {
    if (this.chartResizeHandler) window.removeEventListener('resize', this.chartResizeHandler)
    if (this.industryChartInst) {
      this.industryChartInst.dispose()
      this.industryChartInst = null
    }
  },
  methods: {
    async loadData() {
      try {
        const [overviewRes, industryRes] = await Promise.all([
          getDashboardUserOverview(),
          getDashboardUserIndustryChart(),
        ])
        if (overviewRes && overviewRes.data) {
          this.panels = overviewRes.data.panels || []
        }
        if (industryRes && industryRes.data) {
          this.industryChart = industryRes.data
        }
      } catch (e) {
        ElMessage.error(e.message || '加载用户概况失败')
      }
      this.$nextTick(() => this.initIndustryChart())
    },
    fmtNum(n) {
      const v = Number(n)
      if (Number.isNaN(v)) return '0'
      return v.toLocaleString('en-US')
    },
    donutStyle(items) {
      let acc = 0
      const stops = (items || []).map(item => {
        const start = acc
        acc += Number(item.ratio) || 0
        return `${this.itemMeta[item.key].ratioColor} ${start}% ${acc}%`
      })
      return {
        background: stops.length ? `conic-gradient(${stops.join(', ')})` : '#f0f0f0',
      }
    },
    initIndustryChart() {
      const dom = this.$refs.industryChart
      if (!dom) return
      if (this.industryChartInst) this.industryChartInst.dispose()
      this.industryChartInst = markRaw(echarts.init(dom))
      const { categories, rows } = this.industryChart
      const leaseData = rows.map(r => r.leasePercent)
      const buyoutData = rows.map(r => r.buyoutPercent)

      this.industryChartInst.setOption({
        grid: { left: 52, right: 24, top: 72, bottom: 56 },
        legend: {
          bottom: 4,
          left: 0,
          itemWidth: 8,
          itemHeight: 8,
          itemGap: 20,
          textStyle: { fontSize: 13, color: '#595959' },
          data: ['租赁新增客户', '买断新增客户'],
        },
        tooltip: {
          trigger: 'axis',
          axisPointer: { type: 'shadow' },
          formatter: params => {
            const idx = params[0].dataIndex
            const row = rows[idx]
            return `${categories[idx]}<br/>租赁新增客户：${row.leaseCount} 户（${row.leasePercent}%）<br/>买断新增客户：${row.buyoutCount} 户（${row.buyoutPercent}%）`
          },
        },
        xAxis: {
          type: 'category',
          data: categories,
          axisLine: { lineStyle: { color: '#e8e8e8' } },
          axisTick: { show: false },
          axisLabel: { fontSize: 13, color: '#595959', interval: 0 },
        },
        yAxis: {
          type: 'value',
          max: 100,
          axisLabel: { formatter: '{value}%', fontSize: 13, color: '#8c8c8c' },
          splitLine: { lineStyle: { type: 'dashed', color: '#f0f0f0' } },
          axisLine: { show: false },
          axisTick: { show: false },
        },
        series: [
          {
            name: '租赁新增客户',
            type: 'bar',
            data: leaseData,
            barMaxWidth: 14,
            itemStyle: { color: '#fa8c16', borderRadius: [4, 4, 0, 0] },
            label: {
              show: true,
              position: 'top',
              distance: 10,
              backgroundColor: '#fff',
              borderRadius: 4,
              padding: [6, 8],
              shadowBlur: 6,
              shadowColor: 'rgba(0,0,0,0.08)',
              lineHeight: 16,
              formatter: p => {
                const row = rows[p.dataIndex]
                return `{o|${row.leaseCount} 户 ${row.leasePercent}%}\n{b|${row.buyoutCount} 户 ${row.buyoutPercent}%}`
              },
              rich: {
                o: { color: '#fa8c16', fontSize: 12, fontWeight: 600, lineHeight: 18 },
                b: { color: '#1890ff', fontSize: 12, fontWeight: 600, lineHeight: 18 },
              },
            },
          },
          {
            name: '买断新增客户',
            type: 'bar',
            data: buyoutData,
            barMaxWidth: 14,
            itemStyle: { color: '#1890ff', borderRadius: [4, 4, 0, 0] },
          },
        ],
      })
    },
  },
}
</script>

<style scoped>
/* 文字层级：与租赁业务概况一致 */
.field-label {
  color: #8c8c8c;
  font-size: 13px;
  font-weight: 400;
}

.field-value {
  color: #262626;
  font-size: 14px;
  font-weight: 600;
}

.field-unit {
  color: #262626;
  font-size: 14px;
  font-weight: 400;
}

.user-overview {
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  overflow-x: hidden;
}

.row-3 {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  width: 100%;
}

.row-3 > .user-panel {
  min-width: 0;
}

.section-title {
  display: flex;
  align-items: center;
  margin: 0 0 12px;
  font-size: 16px;
  font-weight: 600;
  color: #262626;
  line-height: 1.4;
}

.title-bar {
  display: inline-block;
  width: 4px;
  height: 16px;
  margin-right: 8px;
  background: #1890ff;
  border-radius: 1px;
  flex-shrink: 0;
}

.title-bar--orange {
  background: #fa8c16;
}

.user-panel {
  background: #f0f6ff;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  padding: 14px 16px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
  min-width: 0;
  box-sizing: border-box;
}

.user-panel--orange {
  background: #fff7e6;
}

.user-panel-summary {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 6px 8px;
  margin-bottom: 12px;
}

.summary-label {
  font-size: 14px;
  font-weight: 600;
  color: #262626;
}

.summary-num {
  font-size: clamp(22px, 2.2vw, 26px);
  font-weight: 600;
  color: #1890ff;
  line-height: 1.15;
}

.summary-unit {
  font-size: 14px;
  font-weight: 400;
  color: #8c8c8c;
  padding-bottom: 3px;
}

.user-panel-divider {
  height: 1px;
  background: #e8e8e8;
  margin-bottom: 12px;
}

.user-panel-body {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 64px;
  gap: 12px 14px;
  align-items: center;
}

.user-item-list {
  display: flex;
  flex-direction: column;
  gap: 14px;
  min-width: 0;
}

.user-item-row {
  display: grid;
  grid-template-columns: 20px minmax(0, 1fr) auto auto;
  gap: 8px 10px;
  align-items: center;
  min-width: 0;
  line-height: 1.45;
  min-height: 22px;
}

.user-item-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.user-item-label {
  font-size: 13px;
  font-weight: 400;
  color: #595959;
  min-width: 0;
  line-height: 1.4;
}

.user-item-value {
  display: inline-flex;
  align-items: baseline;
  gap: 4px;
  white-space: nowrap;
}

.user-item-value .field-value {
  font-size: 14px;
}

.user-item-ratio {
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
}

.user-donut {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  flex-shrink: 0;
  justify-self: center;
  mask: radial-gradient(circle, transparent 40%, #000 41%);
  -webkit-mask: radial-gradient(circle, transparent 40%, #000 41%);
}

.user-chart-section {
  background: #fff;
  border: 1px solid #e8e8e8;
  border-radius: 4px;
  padding: 16px 18px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
  min-width: 0;
}

.user-chart-section .section-title {
  margin-bottom: 8px;
}

.user-industry-chart {
  width: 100%;
  height: 340px;
  min-height: 280px;
}

@media (max-width: 1100px) {
  .row-3 {
    grid-template-columns: 1fr;
  }

  .user-panel-body {
    grid-template-columns: minmax(0, 1fr) 72px;
  }

  .user-donut {
    width: 72px;
    height: 72px;
  }
}

@media (max-width: 900px) {
  .user-panel-body {
    grid-template-columns: 1fr;
  }

  .user-donut {
    margin: 0 auto;
  }

  .user-item-row {
    grid-template-columns: 22px 1fr;
    grid-template-rows: auto auto;
    gap: 4px 8px;
  }

  .user-item-value,
  .user-item-ratio {
    grid-column: 2;
  }

  .user-item-ratio {
    justify-self: start;
  }
}
</style>
